import React from 'react';
import { ChevronDown, Loader2, Pause, Play, Trash2 } from 'lucide-react';
import { formatTimestamp } from '../recording/useRecorder';
import type { RecordingPhase } from '../recording/useRecordingSession';
import { cn } from '../../components/ui/cn';
import { t } from '../../lib/i18n';
import { MobileIconButton } from './MobileUI';

/**
 * Telefonda kayıt ekranı tam ekran: üstte küçült / durum / sil, altta büyük
 * sayaç ile Duraklat ve "Bitir ve özetle". Masaüstündeki RecordingTopBar'ın
 * yerini alıyor; ortadaki içerik (başlık, canlı transkript, notlar) aynı.
 */

interface TopProps {
  phase: RecordingPhase;
  isPaused: boolean;
  /** Kayıt arka planda sürer, ana ekrana dönülür. */
  onMinimize: () => void;
  onDiscard: () => void;
}

export const MobileRecordingTopBar: React.FC<TopProps> = ({ phase, isPaused, onMinimize, onDiscard }) => {
  const isLive = phase === 'live';
  const isFinishing = phase === 'stopping' || phase === 'summarizing';
  const status = isFinishing
    ? phase === 'summarizing'
      ? t('AI özetliyor…', 'AI is summarizing…')
      : t('Kaydediliyor…', 'Saving…')
    : phase === 'starting'
      ? t('Başlatılıyor', 'Starting')
      : isPaused
        ? t('Duraklatıldı', 'Paused')
        : isLive
          ? t('Kaydediliyor', 'Recording')
          : t('Hazır', 'Ready');
  const recordingNow = isLive && !isPaused;

  return (
    <div className="shrink-0 flex items-center justify-between h-[60px] px-4 select-none">
      <MobileIconButton label={t('Küçült (kayıt arka planda sürer)', 'Minimize (recording continues)')} onClick={onMinimize} data-native-back>
        <ChevronDown className="w-6 h-6" />
      </MobileIconButton>
      <span
        className={cn(
          'flex items-center gap-2 h-8 px-3 rounded-full bg-secondary text-[13px] font-semibold',
          recordingNow ? 'text-record-text' : 'text-muted-foreground'
        )}
      >
        {isFinishing ? (
          <Loader2 className="w-3.5 h-3.5 animate-spin" />
        ) : (
          <span className={cn('w-2 h-2 rounded-full', recordingNow ? 'bg-record animate-pulse' : 'bg-muted-foreground')} />
        )}
        {status}
      </span>
      <MobileIconButton
        label={t('Kaydı sil', 'Discard recording')}
        onClick={onDiscard}
        disabled={isFinishing}
        className="text-muted-foreground"
      >
        <Trash2 className="w-5 h-5" />
      </MobileIconButton>
    </div>
  );
};

interface ControlsProps {
  phase: RecordingPhase;
  isPaused: boolean;
  seconds: number;
  micLevel: number;
  onTogglePause: () => void;
  onStop: () => void;
}

export const MobileRecordingControls: React.FC<ControlsProps> = ({
  phase,
  isPaused,
  seconds,
  micLevel,
  onTogglePause,
  onStop,
}) => {
  const isLive = phase === 'live';
  const isFinishing = phase === 'stopping' || phase === 'summarizing';
  const active = isLive && !isPaused;
  // Seviye göstergesi: mikrofonun anlık seviyesine göre yükselen çubuklar.
  const bars = [0.35, 0.6, 0.9, 0.55, 1, 0.7, 0.45, 0.85, 0.5, 0.95, 0.65, 0.4];

  return (
    <div className="shrink-0 px-5 pt-3 pb-5 border-t border-border bg-background select-none">
      <div className="flex items-center justify-center gap-4">
        <span className="font-mono text-[40px] font-medium tracking-tight tabular-nums text-foreground">
          {formatTimestamp(seconds)}
        </span>
        <span className="flex items-center gap-[3px] h-9" aria-hidden="true">
          {bars.map((weight, index) => (
            <span
              key={index}
              className={cn('w-1 rounded-full transition-[height] duration-150', active ? 'bg-brand' : 'bg-muted-foreground/40')}
              style={{ height: `${active ? Math.max(12, Math.min(100, micLevel * 140 * weight)) : 12}%` }}
            />
          ))}
        </span>
      </div>
      <div className="mt-3 flex items-center gap-3">
        <button
          type="button"
          onClick={onTogglePause}
          disabled={!isLive}
          aria-label={isPaused ? t('Devam et', 'Resume') : t('Duraklat', 'Pause')}
          className="w-[60px] h-[60px] rounded-full bg-secondary text-foreground flex items-center justify-center shrink-0 active:opacity-70 disabled:opacity-40"
        >
          {isPaused ? <Play className="w-6 h-6 fill-current" /> : <Pause className="w-6 h-6 fill-current" />}
        </button>
        <button
          type="button"
          onClick={onStop}
          disabled={!isLive}
          className="flex-1 h-[60px] rounded-full bg-record text-white text-[17px] font-bold flex items-center justify-center gap-2.5 active:opacity-85 disabled:opacity-60"
        >
          {isFinishing ? <Loader2 className="w-5 h-5 animate-spin" /> : <span className="w-3.5 h-3.5 rounded-[3px] bg-white" />}
          {phase === 'summarizing'
            ? t('AI özetliyor…', 'AI is summarizing…')
            : phase === 'stopping'
              ? t('Kaydediliyor…', 'Saving…')
              : t('Bitir ve özetle', 'Finish & summarize')}
        </button>
      </div>
    </div>
  );
};

/** Kayıt sürerken diğer ekranlarda sekme çubuğunun üstündeki şerit. */
export const MobileRecordingStrip: React.FC<{
  title: string;
  seconds: number;
  isPaused: boolean;
  onOpen: () => void;
}> = ({ title, seconds, isPaused, onOpen }) => (
  <button
    type="button"
    onClick={onOpen}
    className="shrink-0 mx-3 mb-2 h-[52px] rounded-2xl border border-border bg-card shadow-lg shadow-black/20 flex items-center gap-3 px-4 text-left active:opacity-80"
  >
    <span className={cn('w-2.5 h-2.5 rounded-full bg-record', !isPaused && 'animate-pulse')} />
    <span className="flex-1 min-w-0 text-[15px] font-semibold text-foreground truncate">
      {isPaused ? t('Kayıt duraklatıldı', 'Recording paused') : title || t('Kayıt sürüyor', 'Recording')}
    </span>
    <span className="font-mono text-[15px] tabular-nums text-record-text">{formatTimestamp(seconds)}</span>
  </button>
);
